/**
 * ExecutionsByStatusPage — filter form + paginated results.
 *
 * Wraps POST /executions/by-status. All input state (form fields, date
 * range, property filter tokens, pagination, submitted/committed flags)
 * lives in the executionsByStatus Redux slice; this page component is a
 * thin projection that dispatches actions on user events and renders
 * from the store.
 *
 * Sort is handled server-side — `form.sortBy` goes on the wire in the
 * request payload; the Table's `sortingColumn` prop just controls which
 * arrow icon the column header renders.
 */
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@cloudscape-design/components/alert';
import Autosuggest from '@cloudscape-design/components/autosuggest';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import DateRangePicker from '@cloudscape-design/components/date-range-picker';
import FormField from '@cloudscape-design/components/form-field';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';

import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  SortField,
  clearExecutionsByStatus,
  commit,
  fetchExecutionsByStatus,
  markSubmitted,
  resetAll,
  retry,
  setCurrentPage,
  setDataSource,
  setDateRange,
  setPropertyQuery,
  setSortBy,
  setStateFilter,
  setStatus,
} from 'src/store/slices/executionsByStatus/executionsByStatusSlice';

import { EXECUTIONS_BY_STATUS_COLUMN_DEFINITIONS } from './executionsByStatusColumns';
import {
  DATA_SOURCE_SUGGESTIONS,
  DEFAULT_PAGE_SIZE,
  FILTERING_PROPERTIES,
  RELATIVE_DATE_OPTIONS,
  STATE_SUGGESTIONS,
  STATUS_SUGGESTIONS,
  buildRequest,
  validate,
} from './filterFormHelpers';

const toOptions = (values: string[]) => values.map((value) => ({ value }));

const UPDATED_AT_COLUMN = EXECUTIONS_BY_STATUS_COLUMN_DEFINITIONS.find((c) => c.id === 'updatedAt')!;

/** 4-column asymmetric grid: 3 fields at colspan 3, action pair at colspan 3. */
const TOP_FORM_GRID = [
  { colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 },
];

/** Table filter row: date range (~1/3) + property filter (~2/3). */
const TABLE_FILTER_GRID = [
  { colspan: { default: 12, xs: 4 } },
  { colspan: { default: 12, xs: 8 } },
];

export const ExecutionsByStatusPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    data, loading, error,
    baseForm, dateRange, propertyQuery, submitted, currentPage, committed,
  } = useAppSelector((s) => s.executionsByStatus);

  const pageSize = DEFAULT_PAGE_SIZE;
  const errors = submitted ? validate(baseForm, dateRange).errors : {};

  // Fetch driver — fires whenever the committed snapshot or page changes.
  useEffect(() => {
    if (!committed) return;
    dispatch(fetchExecutionsByStatus(
      buildRequest(committed.baseForm, committed.dateRange, committed.propertyQuery, currentPage, pageSize),
    ));
  }, [committed, currentPage, pageSize, dispatch]);

  // Clear server state on unmount so a return visit starts clean.
  useEffect(() => () => { dispatch(clearExecutionsByStatus()); }, [dispatch]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.totalExecutionsCount / pageSize)) : 0),
    [data, pageSize],
  );

  const handleSearch = () => {
    if (validate(baseForm, dateRange).ok) {
      dispatch(commit());
    } else {
      dispatch(markSubmitted());
    }
  };

  const executions = data?.executions ?? [];
  const total = data?.totalExecutionsCount ?? 0;

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <BreadcrumbGroup
          items={[
            { text: 'Beacon', href: '/' },
            { text: 'Filter executions by status', href: '/executions/by-status' },
          ]}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
        />

        <Header variant="h1">Filter executions by status</Header>

        <Container>
          <SpaceBetween size="m">
            <Grid gridDefinition={TOP_FORM_GRID}>
              <FormField label="Status" errorText={errors.status}>
                <Autosuggest
                  value={baseForm.status}
                  onChange={({ detail }) => dispatch(setStatus(detail.value))}
                  options={toOptions(STATUS_SUGGESTIONS)}
                  enteredTextLabel={(v) => `Use "${v}"`}
                  placeholder="e.g. SEND_TO_BANK"
                  invalid={!!errors.status}
                />
              </FormField>
              <FormField label="State" errorText={errors.state}>
                <Autosuggest
                  value={baseForm.state}
                  onChange={({ detail }) => dispatch(setStateFilter(detail.value))}
                  options={toOptions(STATE_SUGGESTIONS)}
                  enteredTextLabel={(v) => `Use "${v}"`}
                  placeholder="e.g. SUCCESS"
                  invalid={!!errors.state}
                />
              </FormField>
              <FormField label="Data source" errorText={errors.dataSource}>
                <Autosuggest
                  value={baseForm.dataSource}
                  onChange={({ detail }) => dispatch(setDataSource(detail.value))}
                  options={toOptions(DATA_SOURCE_SUGGESTIONS)}
                  enteredTextLabel={(v) => `Use "${v}"`}
                  placeholder="e.g. BIP_V1"
                  invalid={!!errors.dataSource}
                />
              </FormField>
              <FormField label="&nbsp;">
                <SpaceBetween size="xs" direction="horizontal">
                  <Button variant="primary" onClick={handleSearch} disabled={loading}>Search</Button>
                  <Button variant="normal" onClick={() => dispatch(resetAll())} disabled={loading}>Clear</Button>
                </SpaceBetween>
              </FormField>
            </Grid>

            {errors.dateRange && (
              <Alert type="warning" header="Missing date range">{errors.dateRange}</Alert>
            )}
          </SpaceBetween>
        </Container>

        {error && !loading && (
          <Alert
            type="error"
            header="Failed to fetch executions"
            action={<Button onClick={() => dispatch(retry())}>Retry</Button>}
          >
            {error}
          </Alert>
        )}

        {loading && (
          <Box textAlign="center" padding={{ vertical: 'xl' }}>
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <Box color="text-body-secondary">Fetching executions…</Box>
            </SpaceBetween>
          </Box>
        )}

        {!loading && !error && committed && (
          <Table
            variant="container"
            resizableColumns
            items={executions}
            trackBy="id"
            columnDefinitions={EXECUTIONS_BY_STATUS_COLUMN_DEFINITIONS}
            sortingColumn={UPDATED_AT_COLUMN}
            sortingDescending={baseForm.sortBy === 'UPDATED_AT_DESC'}
            onSortingChange={({ detail }) => {
              // Server-side sort — the new sortBy goes on the wire in the
              // next request via the setSortBy → committed.baseForm.sortBy
              // auto-commit path in the slice.
              const nextSort: SortField = detail.isDescending ? 'UPDATED_AT_DESC' : 'UPDATED_AT_ASC';
              dispatch(setSortBy(nextSort));
            }}
            header={
              <Header variant="h2" counter={`(${executions.length} of ${total.toLocaleString()})`}>
                Matching executions
              </Header>
            }
            filter={
              <Grid gridDefinition={TABLE_FILTER_GRID}>
                <DateRangePicker
                  value={dateRange}
                  onChange={({ detail }) => dispatch(setDateRange(detail.value))}
                  relativeOptions={RELATIVE_DATE_OPTIONS}
                  isValidRange={() => ({ valid: true })}
                  placeholder="Updated at range"
                  expandToViewport
                />
                <PropertyFilter
                  query={propertyQuery}
                  onChange={({ detail }) => dispatch(setPropertyQuery(detail))}
                  filteringProperties={FILTERING_PROPERTIES}
                  countText={`${propertyQuery.tokens.length} filter${propertyQuery.tokens.length === 1 ? '' : 's'}`}
                  expandToViewport
                />
              </Grid>
            }
            empty={
              <Box textAlign="center" padding={{ vertical: 'l' }} color="text-body-secondary">
                No executions match the current filters. Widen the date range or remove a filter.
              </Box>
            }
          />
        )}

        {totalPages > 1 && (
          <Box textAlign="center">
            <Pagination
              currentPageIndex={currentPage}
              pagesCount={totalPages}
              disabled={loading}
              onChange={({ detail }) => dispatch(setCurrentPage(detail.currentPageIndex))}
            />
          </Box>
        )}
      </SpaceBetween>
    </Box>
  );
};
